import { useState } from "react";
import { X, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { IconButton, Button } from "./ui/button";

interface AddItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (item: {
    nom: string;
    categorie: string;
    prixVente: number;
    prixCoutant: number;
    quantite: number;
    statut: "Actif" | "Inactif";
    image: string;
    formatQuantite?: number;
    formatUnite?: string;
  }) => void;
  categories: string[];
}

export function AddItemDialog({ isOpen, onClose, onAdd, categories }: AddItemDialogProps) {
  const [nom, setNom] = useState("");
  const [categorie, setCategorie] = useState(categories[0]);
  const [prixVente, setPrixVente] = useState("");
  const [prixCoutant, setPrixCoutant] = useState("");
  const [quantite, setQuantite] = useState("");
  const [statut, setStatut] = useState<"Actif" | "Inactif">("Actif");
  const [imageUrl, setImageUrl] = useState("");
  const [formatQuantite, setFormatQuantite] = useState("");
  const [formatUnite, setFormatUnite] = useState("ml");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!nom || !prixVente || !prixCoutant || !quantite) {
      toast.warning("Veuillez remplir tous les champs requis");
      return;
    }

    onAdd({
      nom,
      categorie,
      prixVente: parseFloat(prixVente),
      prixCoutant: parseFloat(prixCoutant),
      quantite: parseInt(quantite),
      statut,
      image: imageUrl || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400",
      formatQuantite: formatQuantite ? parseFloat(formatQuantite) : undefined,
      formatUnite: formatQuantite ? formatUnite : undefined,
    });

    // Reset form
    setNom("");
    setCategorie(categories[0]);
    setPrixVente("");
    setPrixCoutant("");
    setQuantite("");
    setStatut("Actif");
    setImageUrl("");
    setFormatQuantite("");
    setFormatUnite("ml");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-2xl bg-[var(--card)] rounded-xl shadow-2xl border border-[var(--border)] animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--switch-background)]">
          <h2 className="text-2xl font-semibold text-[var(--foreground)]">Ajouter un article</h2>
          <IconButton
            icon={X}
            variant="ghost"
            onClick={onClose}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nom */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Nom de l'article *
              </label>
              <input
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all placeholder-[var(--muted-foreground-light)]"
                placeholder="Ex: Filet de bœuf"
                required
              />
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Catégorie *
              </label>
              <select
                value={categorie}
                onChange={(e) => setCategorie(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all"
                required
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Statut */}
            <div>
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Statut *
              </label>
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value as "Actif" | "Inactif")}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all"
                required
              >
                <option value="Actif">Actif</option>
                <option value="Inactif">Inactif</option>
              </select>
            </div>

            {/* Prix coûtant */}
            <div>
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Prix coûtant ($) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={prixCoutant}
                onChange={(e) => setPrixCoutant(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all placeholder-[var(--muted-foreground-light)]"
                placeholder="0.00"
                required
              />
            </div>

            {/* Prix de vente */}
            <div>
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Prix de vente ($) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={prixVente}
                onChange={(e) => setPrixVente(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all placeholder-[var(--muted-foreground-light)]"
                placeholder="0.00"
                required
              />
            </div>

            {/* Quantité */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Quantité en stock *
              </label>
              <input
                type="number"
                min="0"
                value={quantite}
                onChange={(e) => setQuantite(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all placeholder-[var(--muted-foreground-light)]"
                placeholder="0"
                required
              />
            </div>

            {/* Format Quantité */}
            <div>
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Format Quantité
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formatQuantite}
                onChange={(e) => setFormatQuantite(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all placeholder-[var(--muted-foreground-light)]"
                placeholder="0.00"
              />
            </div>

            {/* Format Unité */}
            <div>
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Format Unité
              </label>
              <select
                value={formatUnite}
                onChange={(e) => setFormatUnite(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all"
              >
                <option value="ml">ml</option>
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="l">l</option>
                <option value="unité">unité</option>
              </select>
            </div>

            {/* URL Image */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                URL de l'image (optionnel)
              </label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--input-background)] text-[var(--foreground)] border border-[var(--switch-background)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all placeholder-[var(--muted-foreground-light)]"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-[var(--switch-background)]">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Plus}
            >
              Ajouter
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}